import { normalizeTrainingMode } from './trainingModes';

export const CUSTOM_REVIEW_STORAGE_KEY = 'lexilab.custom-review-selection';
export const CUSTOM_REVIEW_MAX_SELECTIONS = 200;

const cleanString = (value) => String(value ?? '').trim();

export const customReviewCandidateKey = (candidate) => {
  const word = cleanString(candidate?.word).toLocaleLowerCase();
  const senseId = cleanString(candidate?.sense_id || candidate?.senseId || 'primary') || 'primary';
  return `${word}::${senseId}`;
};

export const normalizeReviewCandidate = (candidate, fallbackMode = 'meaning') => {
  const source = candidate && typeof candidate === 'object' ? candidate : {};
  const learnedDate = cleanString(source.learned_date || source.learnedDate) || null;
  const nextReviewDate = cleanString(source.next_review_date || source.nextReviewDate) || null;
  return {
    ...source,
    word: cleanString(source.word),
    sense_id: cleanString(source.sense_id || source.senseId || 'primary') || 'primary',
    definition_cn: cleanString(source.definition_cn || source.definition?.cn || source.definition?.zh),
    definition_en: cleanString(source.definition_en || source.definition?.en),
    learned_date: learnedDate,
    next_review_date: nextReviewDate,
    due: source.due === true || source.due === 1 || source.due === '1',
    training_mode: normalizeTrainingMode(source.training_mode || source.trainingMode, fallbackMode),
  };
};

export const normalizeReviewCandidates = (items, fallbackMode = 'meaning') => (
  (Array.isArray(items) ? items : [])
    .map((item) => normalizeReviewCandidate(item, fallbackMode))
    .filter((item) => item.word)
);

const compareDatesDescending = (left, right) => {
  if (left === right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return right.localeCompare(left);
};

export const groupReviewCandidatesByDate = (items) => {
  const groups = new Map();
  normalizeReviewCandidates(items).forEach((item) => {
    const date = item.learned_date || null;
    const key = date || '__unknown__';
    if (!groups.has(key)) groups.set(key, { date, items: [] });
    groups.get(key).items.push(item);
  });
  return [...groups.values()]
    .sort((left, right) => compareDatesDescending(left.date, right.date))
    .map((group) => ({ ...group, count: group.items.length }));
};

export const filterReviewCandidates = (items, query = '') => {
  const normalizedQuery = cleanString(query).toLocaleLowerCase();
  const normalizedItems = normalizeReviewCandidates(items);
  if (!normalizedQuery) return normalizedItems;
  return normalizedItems.filter((item) => [
    item.word,
    item.definition_cn,
    item.definition_en,
  ].some((value) => cleanString(value).toLocaleLowerCase().includes(normalizedQuery)));
};

export const selectedReviewCandidates = (items, selectedKeys) => {
  const keys = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);
  return normalizeReviewCandidates(items).filter((item) => keys.has(customReviewCandidateKey(item)));
};

export const toggleReviewCandidateKey = (selectedKeys, key, max = CUSTOM_REVIEW_MAX_SELECTIONS) => {
  const next = new Set(selectedKeys instanceof Set ? selectedKeys : selectedKeys || []);
  if (next.has(key)) {
    next.delete(key);
    return { selectedKeys: next, blocked: false };
  }
  if (next.size >= max) return { selectedKeys: next, blocked: true };
  next.add(key);
  return { selectedKeys: next, blocked: false };
};

export const selectReviewCandidates = (selectedKeys, candidates, max = CUSTOM_REVIEW_MAX_SELECTIONS) => {
  const next = new Set(selectedKeys instanceof Set ? selectedKeys : selectedKeys || []);
  let blocked = false;
  normalizeReviewCandidates(candidates).forEach((item) => {
    const key = customReviewCandidateKey(item);
    if (next.has(key)) return;
    if (next.size >= max) {
      blocked = true;
      return;
    }
    next.add(key);
  });
  return { selectedKeys: next, blocked };
};

export const buildCustomReviewPracticePayload = ({ items, tier, training_mode, session_id, trainingMode = training_mode, sessionId = session_id } = {}) => ({
  items: normalizeReviewCandidates(items, trainingMode),
  tier,
  mode: 'review',
  session_id: sessionId,
  training_mode: normalizeTrainingMode(trainingMode),
  review_source: 'custom',
});

export const buildCustomReviewStoragePayload = ({ items, trainingMode, tier, sessionId } = {}) => ({
  version: 1,
  items: normalizeReviewCandidates(items, trainingMode).slice(0, CUSTOM_REVIEW_MAX_SELECTIONS),
  training_mode: normalizeTrainingMode(trainingMode),
  tier: cleanString(tier),
  session_id: cleanString(sessionId),
  created_at: new Date().toISOString(),
});

export const matchingCustomReviewResume = (selection, resume) => {
  if (!selection?.session_id || resume?.sessionId !== selection.session_id
    || resume?.custom_review !== true || resume?.sessionStatus !== 'learning'
    || !Array.isArray(resume?.learningItems) || !resume?.wordProgress) return null;
  return resume;
};

const resolveStorage = (storage) => {
  if (storage) return storage;
  try { return typeof window !== 'undefined' ? window.sessionStorage : null; } catch (_) { return null; }
};

export const writeCustomReviewSelection = (payload, storage) => {
  const normalized = buildCustomReviewStoragePayload(payload);
  if (!normalized.items.length) return false;
  try {
    const target = resolveStorage(storage);
    if (!target) return false;
    target.setItem(CUSTOM_REVIEW_STORAGE_KEY, JSON.stringify(normalized));
    return true;
  } catch (_) {
    return false;
  }
};

export const readCustomReviewSelection = (storage) => {
  try {
    const target = resolveStorage(storage);
    if (!target) return null;
    const raw = target.getItem(CUSTOM_REVIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const items = normalizeReviewCandidates(parsed?.items, parsed?.training_mode);
    if (!items.length || items.length > CUSTOM_REVIEW_MAX_SELECTIONS) return null;
    return {
      ...parsed,
      items,
      training_mode: normalizeTrainingMode(parsed?.training_mode),
      tier: cleanString(parsed?.tier),
    };
  } catch (_) {
    return null;
  }
};

export const clearCustomReviewSelection = (storage) => {
  try {
    const target = resolveStorage(storage);
    if (target) target.removeItem(CUSTOM_REVIEW_STORAGE_KEY);
  } catch (_) {}
};
